import React, { useEffect, useMemo, useState } from "react"

interface OutlinedTextBaseProps {
    text: string
    strokeColor?: string
    strokeWidth?: number
    fillColor?: string
    fontSize?: number | string
    fontFamily?: string
    style?: React.CSSProperties
    className?: string
    // 新增属性：是否启用环形文字、环形半径
    circular?: boolean
    circleRadius?: number
    // 新增属性：渐变配置 { from: string, to: string }
    fillGradient?: { from: string; to: string }
    // 新增属性：双层边框 - 外层边框颜色和宽度
    outerStrokeColor?: string
    outerStrokeWidth?: number
    /** 单行模式下 SVG CSS 宽度上限（设计稿 px），超出时按实测宽度等比缩放 */
    fitWidth?: number
}

interface OutlinedTextWithWrapProps extends OutlinedTextBaseProps {
    /** 是否启用换行 */
    wrap: true
    /** 最大宽度，超过此宽度时自动换行 */
    maxWidth: number
}

interface OutlinedTextWithoutWrapProps extends OutlinedTextBaseProps {
    /** 是否启用换行 */
    wrap?: false
    /** 最大宽度，超过此宽度时自动换行 */
    maxWidth?: never
}

export type OutlinedTextProps =
    | OutlinedTextWithWrapProps
    | OutlinedTextWithoutWrapProps

const OutlinedText: React.FC<OutlinedTextProps> = ({
    text,
    strokeColor = "#000",
    strokeWidth = 2,
    fillColor = "#fff",
    fontSize = 20,
    fontFamily = "ShanHaiJiGuYuZhiQingKe",
    style = {},
    className = "",
    circular = false,
    circleRadius,
    fillGradient,
    wrap = false,
    maxWidth,
    outerStrokeColor,
    outerStrokeWidth,
    fitWidth,
}) => {
    const [fontLoadVersion, setFontLoadVersion] = useState(0)
    const fontSizeNum = Number(fontSize)
    const strokeWidthNum = Number(strokeWidth)
    const outerStrokeWidthNum = outerStrokeWidth ? Number(outerStrokeWidth) : 0
    const hasDoubleStroke = !!outerStrokeColor && outerStrokeWidthNum > 0
    // padding 用于给 stroke 留出空间
    // SVG 的 stroke 是向外扩展的，向外扩展 strokeWidth/2
    // 所以 padding 需要 strokeWidth 来容纳完整的 stroke
    // 对于双层边框，padding 应该基于外层边框（因为外层边框在最外层）
    // 对于单层边框，padding 基于内层边框即可
    const paddingLeft =
        (hasDoubleStroke ? outerStrokeWidthNum : strokeWidthNum) + 2
    const paddingRight = hasDoubleStroke ? outerStrokeWidthNum : strokeWidthNum // 右侧减少 padding，让组件可以紧邻
    const paddingTop = hasDoubleStroke ? outerStrokeWidthNum : strokeWidthNum
    const paddingBottom = hasDoubleStroke ? outerStrokeWidthNum : strokeWidthNum
    // 宽度计算：所有字符都按 fontSize 计算，确保宽度足够，避免被裁剪
    const textLength = String(text).length
    const estimatedWidth = textLength * fontSizeNum
    // 宽度 = 文本估算宽度 + 左右 padding
    // padding 已经包含了 stroke 向外扩展的空间，不需要额外增加
    const width = estimatedWidth + paddingLeft + paddingRight
    // 高度 = 字体大小 + 上下 padding
    // padding 已经包含了 stroke 向外扩展的空间，不需要额外增加
    const height = fontSizeNum + paddingTop + paddingBottom

    const baseFontSize = 16 // 设计稿基准字体大小

    // 检查是否需要使用渐变
    const useGradient = !!fillGradient
    const gradientId = useMemo(
        () => `text-gradient-${Math.random().toString(36).substr(2, 9)}`,
        [],
    )

    useEffect(() => {
        if (typeof document === "undefined" || !document.fonts) return

        let cancelled = false
        const fontFaceSet = document.fonts
        const fontCheckValue = `${fontSizeNum}px ${fontFamily}`

        try {
            if (fontFaceSet.check(fontCheckValue)) return
        } catch {}

        fontFaceSet.ready.then(() => {
            if (!cancelled) {
                setFontLoadVersion((version) => version + 1)
            }
        })

        return () => {
            cancelled = true
        }
    }, [fontFamily, fontSizeNum])

    // 如果启用了换行，计算文本行
    const { lines, actualWidth, actualHeight, textCenterX, svgViewBoxWidth } =
        useMemo(() => {
            // 无需测量的简单路径
            if (!wrap && !fitWidth) {
                return {
                    lines: [text],
                    actualWidth: width,
                    actualHeight: height,
                    textCenterX: width / 2,
                    svgViewBoxWidth: width,
                }
            }

            // 需要 DOM 测量（fitWidth 单行模式 或 wrap 模式）
            const svgNS = "http://www.w3.org/2000/svg"
            const tempSvg = document.createElementNS(svgNS, "svg")
            const tempText = document.createElementNS(svgNS, "text")
            tempSvg.setAttribute(
                "data-font-load-version",
                String(fontLoadVersion),
            )
            tempText.setAttribute("font-size", String(fontSizeNum))
            tempText.setAttribute("font-family", fontFamily)
            tempSvg.appendChild(tempText)
            document.body.appendChild(tempSvg)

            try {
                // fitWidth 单行模式：精确测量文本宽度，CSS 宽度不超过 fitWidth
                if (!wrap || !maxWidth) {
                    tempText.textContent = text
                    const measuredTextWidth = tempText.getComputedTextLength()
                    const naturalViewBoxWidth =
                        measuredTextWidth + paddingLeft + paddingRight
                    const cssDisplayWidth = Math.min(
                        naturalViewBoxWidth,
                        fitWidth!,
                    )
                    return {
                        lines: [text],
                        actualWidth: cssDisplayWidth,
                        actualHeight: height,
                        textCenterX: naturalViewBoxWidth / 2,
                        svgViewBoxWidth: naturalViewBoxWidth,
                    }
                }

                // wrap 模式（原有逻辑）
                // maxWidth 是设计稿像素值，最终由 rem 跟随根字号统一缩放
                const availableWidth = maxWidth - paddingLeft - paddingRight
                const resultLines: string[] = []
                let currentLine = ""

                // 判断是否为数字字符，用于换行时避免把数字从中折断
                const isDigit = (c: string) => /^\d$/.test(c)

                for (let i = 0; i < text.length; i++) {
                    const char = text[i]

                    // 如果当前字符是换行符，直接换行
                    if (char === "\n") {
                        if (currentLine) {
                            resultLines.push(currentLine)
                            currentLine = ""
                        }
                        continue
                    }

                    // 测量当前行加上新字符后的宽度
                    tempText.textContent = currentLine + char
                    const newWidth = tempText.getComputedTextLength()

                    // 如果加上当前字符会超过宽度，先保存当前行
                    if (newWidth > availableWidth && currentLine) {
                        // 若当前行末尾是数字且新字符也是数字，则不在数字中间断行，把整段数字移到下一行
                        if (
                            isDigit(char) &&
                            isDigit(currentLine[currentLine.length - 1])
                        ) {
                            let digitStart = currentLine.length - 1
                            while (
                                digitStart > 0 &&
                                isDigit(currentLine[digitStart - 1])
                            ) {
                                digitStart--
                            }
                            resultLines.push(currentLine.slice(0, digitStart))
                            currentLine = currentLine.slice(digitStart) + char
                        } else {
                            // 尝试在最近的空格处断行（英文单词边界）
                            const lastSpace = currentLine.lastIndexOf(" ")
                            if (lastSpace > 0) {
                                resultLines.push(
                                    currentLine.slice(0, lastSpace),
                                )
                                currentLine =
                                    currentLine.slice(lastSpace + 1) + char
                            } else {
                                resultLines.push(currentLine)
                                currentLine = char
                            }
                        }
                    } else {
                        currentLine += char
                    }
                }

                // 添加最后一行
                if (currentLine) {
                    resultLines.push(currentLine)
                }

                const finalLines = resultLines.length > 0 ? resultLines : [text]
                const lineHeight = fontSizeNum * 1.0 // 减小行间距，从1.2改为1.0
                const finalWidth = maxWidth
                // 计算文本居中的 x 坐标：使用 SVG 容器的中心点
                const textCenterX = finalWidth / 2
                const finalHeight =
                    paddingTop +
                    fontSizeNum +
                    (finalLines.length - 1) * lineHeight +
                    paddingBottom

                return {
                    lines: finalLines,
                    actualWidth: finalWidth,
                    actualHeight: finalHeight,
                    textCenterX,
                    svgViewBoxWidth: finalWidth,
                }
            } finally {
                document.body.removeChild(tempSvg)
            }
        }, [
            text,
            wrap,
            maxWidth,
            fitWidth,
            fontSizeNum,
            fontFamily,
            fontLoadVersion,
            paddingLeft,
            paddingRight,
            paddingTop,
            paddingBottom,
            width,
            height,
        ])

    const keyedLines = useMemo(() => {
        const lineCounts = new Map<string, number>()

        return lines.map((line) => {
            const count = lineCounts.get(line) ?? 0
            lineCounts.set(line, count + 1)

            return {
                key: `${line}-${count}`,
                value: line,
            }
        })
    }, [lines])

    const lineHeight = fontSizeNum * 1.0 // 行高为字体大小的1.0倍（减小行间距）
    const widthRem = actualWidth / baseFontSize
    const heightRem = actualHeight / baseFontSize

    const isMobile =
        (typeof document !== "undefined" &&
            document.body?.dataset.rotate === "1") ||
        (typeof navigator !== "undefined" &&
            /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent))
    const dominantBaselineValue = wrap && maxWidth ? "hanging" : "middle"
    const firstLineDy = isMobile ? "0.18em" : 0

    // 环形文字相关参数
    const defaultRadius =
        Math.max(width, height) / 2 -
        fontSizeNum / 2 -
        Math.max(paddingLeft, paddingRight)
    const radius = circleRadius || defaultRadius
    const centerX = width / 2
    const centerY = height / 2 + radius
    const pathId = useMemo(
        () =>
            `outlined-text-circle-path-${Math.random().toString(36).substr(2, 9)}`,
        [],
    )

    if (circular) {
        // 环形文字渲染（不支持换行）
        const circularWidthRem = width / baseFontSize
        const circularHeightRem = height / baseFontSize
        return (
            <svg
                style={{
                    display: "inline-block",
                    verticalAlign: "middle",
                    width: `${circularWidthRem}rem`,
                    height: `${circularHeightRem}rem`,
                    pointerEvents: "none",
                    ...style,
                }}
                className={className}
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="xMidYMid meet"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <path
                        id={pathId}
                        d={`M ${centerX - radius},${centerY} a ${radius},${radius} 0 1,1 ${radius * 2},0 a ${radius},${radius} 0 1,1 -${radius * 2},0`}
                        fill="none"
                    />
                    {useGradient && fillGradient && (
                        <linearGradient
                            id={gradientId}
                            x1="0%"
                            y1="0%"
                            x2="0%"
                            y2="100%"
                        >
                            <stop offset="0%" stopColor={fillGradient.from} />
                            <stop offset="100%" stopColor={fillGradient.to} />
                        </linearGradient>
                    )}
                </defs>
                {/* 外层边框（如果启用双层边框） */}
                {hasDoubleStroke && (
                    <text
                        stroke={outerStrokeColor}
                        strokeWidth={outerStrokeWidthNum}
                        fill={"none"}
                        fontSize={fontSizeNum}
                        fontFamily={fontFamily}
                        strokeLinejoin="round"
                        style={{ pointerEvents: "none", userSelect: "none" }}
                    >
                        <textPath
                            xlinkHref={`#${pathId}`}
                            startOffset="25%"
                            textAnchor="middle"
                            dominantBaseline="central"
                        >
                            {text}
                        </textPath>
                    </text>
                )}
                {/* 内层边框 */}
                <text
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    fill={"none"}
                    fontSize={fontSizeNum}
                    fontFamily={fontFamily}
                    strokeLinejoin="round"
                    style={{ pointerEvents: "none", userSelect: "none" }}
                >
                    <textPath
                        xlinkHref={`#${pathId}`}
                        startOffset="25%"
                        textAnchor="middle"
                        dominantBaseline="central"
                    >
                        {text}
                    </textPath>
                </text>
                {/* 填充层 */}
                <text
                    fill={useGradient ? `url(#${gradientId})` : fillColor}
                    fontSize={fontSizeNum}
                    fontFamily={fontFamily}
                    style={{ pointerEvents: "none", userSelect: "none" }}
                >
                    <textPath
                        xlinkHref={`#${pathId}`}
                        startOffset="25%"
                        textAnchor="middle"
                        dominantBaseline="central"
                    >
                        {text}
                    </textPath>
                </text>
            </svg>
        )
    }

    return (
        <svg
            style={{
                display: "inline-block",
                verticalAlign: "middle",
                width: `${widthRem}rem`,
                height: `${heightRem}rem`,
                pointerEvents: "none",
                ...style,
            }}
            className={className}
            viewBox={`0 0 ${svgViewBoxWidth} ${actualHeight}`}
            preserveAspectRatio={"xMidYMid meet"}
            xmlns="http://www.w3.org/2000/svg"
        >
            {useGradient && fillGradient && (
                <defs>
                    <linearGradient
                        id={gradientId}
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                    >
                        <stop offset="0%" stopColor={fillGradient.from} />
                        <stop offset="100%" stopColor={fillGradient.to} />
                    </linearGradient>
                </defs>
            )}
            {/* 外层边框（如果启用双层边框） */}
            {hasDoubleStroke && (
                <text
                    x={textCenterX}
                    y={wrap && maxWidth ? paddingTop : actualHeight / 2}
                    textAnchor="middle"
                    stroke={outerStrokeColor}
                    strokeWidth={outerStrokeWidthNum}
                    fill={"none"}
                    fontSize={fontSizeNum}
                    fontFamily={fontFamily}
                    dominantBaseline={dominantBaselineValue}
                    strokeLinejoin="round"
                    style={{ pointerEvents: "none", userSelect: "none" }}
                >
                    {keyedLines.map((line, index) => {
                        return (
                            <tspan
                                key={`outer-${line.key}`}
                                x={textCenterX}
                                dy={index === 0 ? firstLineDy : lineHeight}
                                textAnchor="middle"
                            >
                                {line.value}
                            </tspan>
                        )
                    })}
                </text>
            )}
            {/* 内层边框 */}
            <text
                x={textCenterX}
                y={wrap && maxWidth ? paddingTop : actualHeight / 2}
                textAnchor="middle"
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                fill={"none"}
                fontSize={fontSizeNum}
                fontFamily={fontFamily}
                dominantBaseline={dominantBaselineValue}
                strokeLinejoin="round"
                style={{ pointerEvents: "none", userSelect: "none" }}
            >
                {keyedLines.map((line, index) => {
                    return (
                        <tspan
                            key={`inner-${line.key}`}
                            x={textCenterX}
                            dy={index === 0 ? firstLineDy : lineHeight}
                            textAnchor="middle"
                        >
                            {line.value}
                        </tspan>
                    )
                })}
            </text>
            {/* 填充层 */}
            <text
                x={textCenterX}
                y={wrap && maxWidth ? paddingTop : actualHeight / 2}
                textAnchor="middle"
                fill={useGradient ? `url(#${gradientId})` : fillColor}
                fontSize={fontSizeNum}
                fontFamily={fontFamily}
                dominantBaseline={dominantBaselineValue}
                style={{ pointerEvents: "none", userSelect: "none" }}
            >
                {keyedLines.map((line, index) => {
                    return (
                        <tspan
                            key={`fill-${line.key}`}
                            x={textCenterX}
                            dy={index === 0 ? firstLineDy : lineHeight}
                            textAnchor="middle"
                        >
                            {line.value}
                        </tspan>
                    )
                })}
            </text>
        </svg>
    )
}

export default OutlinedText
